import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { getNewOpportunitySummaryAPI } from '../../api/newOpportunity';
import type { CenterCoachMetricKey } from '../../api/feedDashboard';
import { useCenterFeatureFlags } from '../../hooks/useCenterFeatureFlags';
import { getItemWithExpiration } from '../../lib/utils';
import {
    CenterCoachRankingResponse,
    CenterCoachRankingTrainer,
    fetchGetCenterCoachRanking,
} from '../../services/useFeedDashboardQueryService';
import CoachMetricEvidenceModal from './CoachMetricEvidenceModal';
import {
    buildClosingRateMetricForCoach,
    buildNewOpportunityReportPath,
    buildNewOpportunitySummaryParams,
    buildStandardCoachPerformanceMetrics,
    ClosingRateState,
    CoachPerformanceCardAction,
    CoachPerformanceMetric,
    formatCoachPerformanceMetricValue,
    getCoachPerformanceCardAction,
    isEvidenceMonthAvailable,
    RE_REGISTRATION_SUCCESS_NAMES,
    NewOpportunitySummaryLike,
} from './coachPerformanceModel';

type Props = {
    centerId: string;
    coachId: string;
    month: string;
};

/**
 * 순위 API는 성공 시 axios 응답을, 401/403/500 이거나 그 밖의 실패에서는
 * {status} 또는 undefined 를 돌려준다. trainers 배열이 있는 형태만 정상 응답으로 본다.
 */
const extractRankingPayload = (response: any): CenterCoachRankingResponse | undefined => {
    const candidates = [response?.data?.data, response?.data, response];
    return candidates.find((candidate) => Array.isArray(candidate?.trainers));
};

const extractCoachSummaries = (response: any): NewOpportunitySummaryLike[] => {
    const candidates = [response?.data?.data, response?.data, response];
    const payload = candidates.find((candidate) => Array.isArray(candidate?.byCoach));
    return payload?.byCoach ?? [];
};

const qualityLabels = {
    EXACT: '정확 데이터',
    ESTIMATED: '추정 데이터',
    MIXED: '혼합 데이터',
} as const;

const EMPTY_TRAINERS: CenterCoachRankingTrainer[] = [];

const CARD_BASE_CLASS =
    'min-w-0 rounded-[16px] border border-[#E2E8F0] bg-white p-[20px] text-left shadow-[0_1px_3px_rgba(26,48,93,0.06)]';

const Skeleton = () => (
    <div
        role="status"
        aria-busy="true"
        aria-label="강사 성과를 불러오는 중"
        className="grid min-w-0 grid-cols-1 gap-[12px] md:grid-cols-2 lg:grid-cols-4"
    >
        {Array.from({ length: 8 }, (_, index) => (
            <div key={index} className="h-[174px] animate-pulse rounded-[16px] border border-[#E2E8F0] bg-white p-[20px]">
                <div className="h-[16px] w-[46%] rounded bg-[#EDF1F6]" />
                <div className="mt-[22px] h-[30px] w-[64%] rounded bg-[#E7ECF3]" />
                <div className="mt-[24px] h-[14px] w-[42%] rounded bg-[#F0F3F7]" />
            </div>
        ))}
    </div>
);

const CoachPerformanceSummary = ({ centerId, coachId, month }: Props) => {
    const navigate = useNavigate();
    const authorization = getItemWithExpiration('authorization');
    const featureFlags = useCenterFeatureFlags();
    const [evidenceMetric, setEvidenceMetric] = useState<CenterCoachMetricKey | null>(null);
    const baseDate = dayjs(month).format('YYYY-MM');

    // onSuccess 는 의도적으로 제외한다. 공용 fetcher 의 onSuccess 는 200이 아니면 throw 하는데,
    // react-query v4 에서 그 예외는 쿼리 에러 상태가 아니라 알림 단계의 미처리 예외가 된다.
    const { queryKey: rankingQueryKey, queryFn: rankingQueryFn } = fetchGetCenterCoachRanking({
        centerId,
        period: 'monthly',
        baseDate,
    });
    const rankingQuery = useQuery({
        queryKey: rankingQueryKey,
        queryFn: rankingQueryFn,
        enabled: !!centerId && !!coachId && !!authorization,
        staleTime: 60 * 1000,
    });

    const summaryParams = useMemo(() => buildNewOpportunitySummaryParams(month), [month]);
    const summaryQuery = useQuery({
        queryKey: ['coachPerformanceNewOpportunitySummary', centerId, summaryParams],
        queryFn: () => getNewOpportunitySummaryAPI(centerId, authorization, summaryParams),
        enabled: featureFlags.isNewOpportunityEnabled && !!centerId && !!coachId && !!authorization,
        staleTime: 60 * 1000,
    });

    const rankingPayload = extractRankingPayload(rankingQuery.data);
    const trainers: CenterCoachRankingTrainer[] = rankingPayload?.trainers ?? EMPTY_TRAINERS;
    const trainer = trainers.find((item) => item.coachId === coachId);
    const standardMetrics = useMemo(
        () => buildStandardCoachPerformanceMetrics(trainers, coachId, rankingPayload?.degraded),
        [trainers, coachId, rankingPayload?.degraded],
    );

    // 신규기회는 별도 API라 여기만 실패해도 나머지 7개 지표는 그대로 보여준다.
    const closingState: ClosingRateState = featureFlags.isError || summaryQuery.isError
        ? 'error'
        : featureFlags.isNewOpportunityEnabled
        ? 'ready'
        : 'featureOff';
    const closingMetric = useMemo(
        () =>
            buildClosingRateMetricForCoach(
                trainers,
                coachId,
                extractCoachSummaries(summaryQuery.data),
                closingState,
            ),
        [trainers, coachId, summaryQuery.data, closingState],
    );
    const metrics = useMemo(() => [...standardMetrics, closingMetric], [standardMetrics, closingMetric]);

    // 응답이 아예 없거나 trainers 형태가 아니면 조용한 실패다. "데이터 없음"과 구분한다.
    const isRankingError = rankingQuery.isError || (rankingQuery.isSuccess && !rankingPayload);
    const isInitialLoading =
        rankingQuery.isInitialLoading || featureFlags.isInitialLoading || summaryQuery.isInitialLoading;
    const isAggregating = dayjs(month).isSame(dayjs(), 'month');
    const isEvidenceAvailable = isEvidenceMonthAvailable(month);

    const retry = () => {
        rankingQuery.refetch();
        featureFlags.refetch();
        if (featureFlags.isNewOpportunityEnabled) summaryQuery.refetch();
    };

    const onCardAction = (metric: CoachPerformanceMetric, action: CoachPerformanceCardAction) => {
        if (action === 'evidence' && metric.evidenceKey) {
            setEvidenceMetric(metric.evidenceKey);
            return;
        }
        if (action === 'newOpportunity') navigate(buildNewOpportunityReportPath(month, coachId));
    };

    if (isInitialLoading) return <Skeleton />;

    if (isRankingError) {
        return (
            <div role="alert" className="rounded-[16px] border border-[#FF9BA1] bg-[#FFE8E9] px-[24px] py-[28px] text-center">
                <p className="text-[16px] font-semibold text-[#3D4244]">강사 성과를 불러오지 못했습니다.</p>
                <p className="mt-[5px] text-[13px] text-[#6E6E6E]">잠시 후 다시 시도해 주세요.</p>
                <button
                    type="button"
                    onClick={retry}
                    className="mt-[16px] rounded-[8px] bg-[#3D73DD] px-[18px] py-[9px] text-[14px] font-semibold text-white"
                >
                    다시 시도
                </button>
            </div>
        );
    }

    if (!trainer) {
        return (
            <div className="rounded-[16px] border border-[#E2E8F0] bg-white px-[24px] py-[42px] text-center">
                <p className="text-[16px] font-semibold text-[#3D4244]">강사 성과 데이터가 없습니다.</p>
                <p className="mt-[5px] text-[13px] text-[#8896B0]">선택한 월에 집계된 강사인지 확인해 주세요.</p>
            </div>
        );
    }

    return (
        <>
            <div className="grid min-w-0 grid-cols-1 gap-[12px] md:grid-cols-2 lg:grid-cols-4">
                {metrics.map((metric) => {
                    const action = getCoachPerformanceCardAction(metric, isEvidenceAvailable);
                    return (
                        <MetricCard
                            key={metric.key}
                            metric={metric}
                            action={action}
                            isAggregating={isAggregating}
                            onAction={() => onCardAction(metric, action)}
                            successAsOf={
                                metric.key === 'reRegistrationSuccessRate'
                                    ? rankingPayload?.reRegistrationSuccessAsOf
                                    : undefined
                            }
                            successDataQuality={
                                metric.key === 'reRegistrationSuccessRate'
                                    ? trainer.snapshot?.reRegistrationSuccessDataQuality
                                    : undefined
                            }
                        />
                    );
                })}
            </div>
            {evidenceMetric && (
                <CoachMetricEvidenceModal
                    centerId={centerId}
                    coachId={coachId}
                    coachName={trainer.name}
                    metricKey={evidenceMetric}
                    month={baseDate}
                    onClose={() => setEvidenceMetric(null)}
                />
            )}
        </>
    );
};

const buildRankText = (metric: CoachPerformanceMetric, action: CoachPerformanceCardAction) => {
    if (metric.rank != null) return `센터 ${metric.rank}/${metric.eligibleCoachCount}위`;
    if (metric.key === 'closingRate') {
        return action === 'newOpportunity' ? '신규기회 분석에서 상세 보기' : '신규기회 분석 이용 불가';
    }
    // 본인 값이 없으면 분모(강사 전원 수)도 내려오지 않는다. 순위 자체가 성립하지 않는 상태다.
    return '센터 순위 제외';
};

/**
 * 값 아래에 붙는 분모 — 「정상 종료 63명 중 30명」.
 *
 * `%` 만 보여주면 3명 중 1명과 300명 중 100명이 같은 카드가 된다.
 * 큰 숫자를 늘리는 대신 바로 아래 줄에 붙여 카드 높이를 지킨다.
 */
const buildBasisText = (metric: CoachPerformanceMetric): string | null => {
    if (metric.key !== 'reRegistrationSuccessRate') return null;
    /*
     * 🔴 분자는 **서버가 준 값**을 읽는다. `value/100 × denominator` 로 되돌려
     *    계산하면 반올림된 %(소수 1자리)에서 사람 수를 만들어내는 셈이라,
     *    47.4% × 63 처럼 경계에서 한 명이 어긋난다. 없는 숫자를 지어내지 않는다.
     */
    if (metric.numerator == null || metric.denominator == null || metric.denominator <= 0) return null;
    return `${RE_REGISTRATION_SUCCESS_NAMES.denominator} ${metric.denominator}명 중 ${metric.numerator}명`;
};

const MetricCard = ({
    metric,
    action,
    isAggregating,
    onAction,
    successAsOf,
    successDataQuality,
}: {
    metric: CoachPerformanceMetric;
    action: CoachPerformanceCardAction;
    isAggregating: boolean;
    onAction: () => void;
    successAsOf?: string;
    successDataQuality?: keyof typeof qualityLabels;
}) => {
    const isUnavailable = metric.value == null;
    const valueText = formatCoachPerformanceMetricValue(metric);
    const rankText = buildRankText(metric, action);
    const basisText = buildBasisText(metric);
    const statusText = isAggregating ? '집계 중' : '집계 완료';
    const metadata = [
        successAsOf && dayjs(successAsOf).isValid() ? `${dayjs(successAsOf).format('YYYY.MM.DD HH:mm')} 기준` : '',
        successDataQuality ? qualityLabels[successDataQuality] : '',
    ].filter(Boolean);

    const body = (
        <>
            <div className="flex items-start justify-between gap-[8px]">
                <span className="min-w-0 text-[14px] font-semibold leading-[20px] text-[#5E7093]">{metric.label}</span>
                <span
                    className={`shrink-0 rounded-full px-[8px] py-[3px] text-[11px] font-semibold ${
                        isAggregating ? 'bg-[#ECF1FC] text-[#3D73DD]' : 'bg-[#E5F8F3] text-[#00A793]'
                    }`}
                >
                    {statusText}
                </span>
            </div>
            <strong
                className={`mt-[14px] block min-h-[38px] break-keep font-bold leading-[38px] ${
                    isUnavailable ? 'text-[20px] text-[#8896B0]' : 'text-[28px] text-[#1A305D]'
                }`}
            >
                {valueText}
            </strong>
            {/* 🔴 「N명 중 M명」 — 분모 없이 %만 보여주지 않는다 */}
            {basisText && (
                <span className="mt-[2px] block text-[12px] leading-[18px] text-[#8896B0]">{basisText}</span>
            )}
            <div className="mt-[12px] min-h-[38px] border-t border-[#F0F3F7] pt-[10px] text-[12px] leading-[18px] text-[#8896B0]">
                {rankText}
                {metadata.length > 0 && (
                    <span className="block truncate" title={metadata.join(' · ')}>
                        {metadata.join(' · ')}
                    </span>
                )}
            </div>
        </>
    );

    // 열어볼 근거가 없는 카드는 조작할 수 없는 통계일 뿐이므로 버튼으로 노출하지 않는다.
    if (action === 'none') {
        return (
            <div className={CARD_BASE_CLASS} data-testid={`coach-metric-${metric.key}`}>
                {body}
            </div>
        );
    }

    return (
        <button
            type="button"
            onClick={onAction}
            data-testid={`coach-metric-${metric.key}`}
            aria-label={`${metric.label} ${valueText}, ${rankText}, ${statusText}. ${
                action === 'evidence' ? '원천 근거 보기' : '신규기회 분석 보기'
            }`}
            className={`${CARD_BASE_CLASS} transition hover:border-[#C3D4F4] hover:shadow-[0_5px_16px_rgba(26,48,93,0.10)] focus:outline-none focus:ring-2 focus:ring-[#C3D4F4]`}
        >
            {body}
        </button>
    );
};

export default CoachPerformanceSummary;
